import type { Messages } from '../catalogue.en'

/**
 * The patient record surface: attachments, their upload, the Group and Content
 * they carry, and the patient EMR journal built on them.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const emr: Partial<Messages> = {
  'protocolChoice.primary': "Основной протокол",
  'protocolChoice.choose': "Выберите протокол",
  'protocolChoice.empty': "Нет активных основных протоколов",
  'protocolChoice.defaultUnavailable': "Основной протокол по умолчанию недоступен. Попросите администратора клиники настроить его или предоставить право выбора протокола.",
  'protocolChoice.replace': "Заменить основной протокол",
  'protocolChoice.replacing': "Замена…",
  'protocolChoice.confirmReplace': "Отбросить содержимое и заменить",
  'protocolChoice.discardDetail': "Всё сохранённое и несохранённое содержимое текущего основного протокола, включая его переводы, будет отброшено из текущей записи. Новый протокол будет пустым. Диагнозы, дополнительные протоколы, услуги, направления и остальные данные записи сохранятся. Прежний протокол останется в истории.",
  'protocolChoice.current': "Текущий протокол",
  'protocolChoice.new': "Новый протокол",
  'protocolChoice.notPrimary': "Этот протокол является дополнительным документом. Выберите основной протокол.",
  'protocolChoice.unchanged': "Этот протокол уже является текущим основным протоколом.",
  'protocolChoice.primaryExists': "В записи уже есть основной протокол. Используйте замену основного протокола.",

  'file.title': 'Файлы',
  'file.tableLabel': 'Файлы пациента',
  'file.columnName': 'Название',
  'file.columnDocumentDate': 'Дата документа',
  'file.columnUploaded': 'Загружено',
  'file.columnAuthor': 'Автор',
  'file.columnStatus': 'Статус',
  'file.columnSize': 'Размер',
  'file.columnLinks': 'Связи с записями клиники',
  'file.columnActions': 'Действия',

  'file.sizeFrom': 'Размер от (КБ)',
  'file.sizeTo': 'Размер до (КБ)',
  'file.linkStateLabel': 'Фильтрация по ссылкам на записи',
  'file.linkStateAll': 'Все файлы',
  'file.linkStateLinked': 'Связано с записью',
  'file.linkStateUnlinked': 'Не связано',

  'file.selectedFile': 'Выбранный файл',

  'file.loading': 'Загрузка файлов',
  'file.unavailable': 'Список файлов недоступен',
  'file.forbidden': 'У вас нет прав на просмотр файлов пациента',
  'file.forbiddenDetail': 'Остальная часть карты пациента по-прежнему доступна.',
  'file.empty': 'У этого пациента нет файлов',
  'file.filteredEmpty': 'Нет файлов, соответствующих этим фильтрам',
  'file.filteredEmptyDetail': 'Сбросьте фильтры, чтобы увидеть все файлы этого пациента.',
  'file.pageEmpty': 'Эта страница результатов больше недоступна',
  'file.pageEmptyDetail': 'Совпадения ещё есть. Вернитесь на первую страницу, чтобы их увидеть.',
  'file.firstPage': 'Вернуться на первую страницу',

  'file.expand': 'Показать сведения о {name}',
  'file.collapse': 'Скрыть сведения о {name}',
  'file.download': 'Скачать {name}',
  'file.preview': 'Просмотреть',
  'file.previewNamed': 'Просмотреть {name}',
  'file.previewTitle': 'Просмотр: {name}',
  'file.previewClose': 'Закрыть',
  'file.previewControls': 'Управление просмотром документа',
  'file.previewPrevious': 'Предыдущая страница',
  'file.previewNext': 'Следующая страница',
  'file.previewPage': 'Страница {page} из {count}',
  'file.previewZoomOut': 'Уменьшить',
  'file.previewZoomIn': 'Увеличить',
  'file.previewLoading': 'Подготовка просмотра…',
  'file.previewUnavailable': 'Не удалось открыть просмотр. Сохранённый файл не изменился.',
  'file.previewCanvas': 'Страница документа {page}',
  'file.downloadFailed': 'Файл {name} не удалось загрузить. Файл остался без изменений.',

  'file.downloadOne': 'Скачать выбранный файл',
  'file.downloadSelected': 'Скачать файлы ({count})',
  'file.downloadEach': 'Скачать отдельно',
  'file.downloadArchive': 'Скачать как один архив',
  'file.downloadWorking': 'Загрузка выбранных файлов…',
  'file.downloadStopped': '{name} не удалось загрузить, поэтому остальные файлы не были получены. Файлы остались без изменений.',
  'file.downloadArchiveFailed': 'Архив не удалось собрать. Файлы остались без изменений.',

  'file.deleteNamed': 'Удалить {name}',
  'file.deleteSelected': 'Удалить выбранное: {count}',
  'file.deleteTitle': 'Удалить выбранные файлы пациента?',
  'file.deleteKeep': 'Оставить файлы',
  'file.deleteConfirm': 'Удалить безвозвратно',
  'file.deleting': 'Удаление…',
  'file.deleteFilesTitle': 'Файлы и производные копии',
  'file.deleteFilesDetail': 'Байты оригиналов и всех обработанных копий будут физически удалены. В журнале останутся запись об удалении и указанная причина.',
  'file.deleteReason': 'Причина удаления',
  'file.deleteReasonHint': 'Обязательна и записывается в журнал вместе с вашим именем.',
  'file.deleteReasonWrongPatient': 'Загружено не тому пациенту',
  'file.deleteReasonDuplicate': 'Дублирующая загрузка',
  'file.deleteReasonMistake': 'Загружено по ошибке',
  'file.deleteReasonOther': 'Другая причина',
  'file.deleteReasonDetail': 'Причина своими словами',
  'file.deleteFailed': 'Не удалось завершить удаление. Уже удалённые элементы исчезнут из списка, остальные останутся доступными.',
  'file.deleteRefusedChanged': 'Строка изменилась, пока этот вопрос был открыт, поэтому она не удалена. Удалённые до неё строки уже удалены; откройте удаление заново и действуйте по тому, чем эта строка стала.',
  'file.deleteRefusedGone': 'Этой строки уже нет, удалять для неё нечего. Удалённые до неё строки тоже удалены.',
  'file.deleteRefusedForbidden': 'У этой учётной записи нет права удалять эту строку. Она не удалена; удалённые до неё строки уже удалены.',
  'file.deleteRefusedInvalid': 'Удаление отклонено в том виде, в каком оно было указано, поэтому строка не удалена. Проверьте причину и повторите; удалённые до неё строки уже удалены.',
  'file.deleteRefusedUnavailable': 'Хранилище не ответило по этой строке, поэтому она не удалена. Удалённые до неё строки уже удалены, попытку стоит повторить.',

  'file.comment': 'Комментарий',
  'file.commentLoading': 'Загрузка сведений',
  'file.commentUnavailable': 'Сведения недоступны',
  'file.noComment': 'Комментарий не записан',
  'file.mediaType': 'Сохранённый тип',
  'file.digest': 'Контрольная сумма',
  'file.noDocumentDate': 'Не записана',
  'file.technicalTitle': 'Технические сведения',

  'file.classificationNotSet': 'Группа и содержимое не заданы',
  'file.classifyAI': 'ИИ-классификация',
  'file.classifyAIRequesting': 'Запрос',
  'file.classifyAIOverwriteTitle': 'Заменить классификацию файла?',
  'file.classifyAIOverwriteDescription':
    'ИИ заново классифицирует файл и может заменить текущие группу и содержимое.',
  'file.classifyAIOverwriteCancel': 'Оставить текущую',
  'file.classifyAIOverwriteConfirm': 'Классифицировать заново',

  'file.editMetadata': 'Изменить сведения',
  'file.editFormLabel': 'Изменение сведений о {name}',
  'file.editTitle': 'Заголовок',
  'file.editTitleHint': 'Оставьте пустым, чтобы показать сохранённое имя файла.',
  'file.editDocumentDate': 'Дата документа',
  'file.editDocumentDateHint': 'Оставьте пустым, если у документа нет даты.',
  'file.editComment': 'Комментарий',
  'file.editSave': 'Сохранить сведения',
  'file.editSaving': 'Сохранение',
  'file.editCancel': 'Отмена',
  'file.editStaleRevision': 'Пока вы редактировали, эти сведения изменил кто-то другой.',
  'file.editStaleRevisionDetail': 'Загрузите сохранённую редакцию, чтобы увидеть, что хранится сейчас, затем примените к ней своё изменение.',
  'file.editReload': 'Загрузить сохранённую редакцию',
  'file.editReloadFailed': 'Не удалось загрузить сохранённую редакцию. Ваши изменения на месте.',
  'file.editInvalid': 'Проверьте сведения и попробуйте снова.',
  'file.editForbidden': 'У вас нет прав на изменение этого файла.',
  'file.editGone': 'Этот файл больше недоступен.',
  'file.editSessionEnded': 'Ваш сеанс завершён. Войдите снова, чтобы продолжить.',
  'file.editUnavailable': 'Сейчас не удалось сохранить сведения. Попробуйте позже.',
  'file.editUnsavedTitle': 'Несохранённые сведения',
  'file.editUnsavedDetail': 'Вы изменили сведения об этом файле и не сохранили их.',
  'file.editKeepEditing': 'Продолжить редактирование',
  'file.editDiscard': 'Отменить изменения',
  'file.unsavedIntent': 'Сведения о файле',

  'file.linksTitle': 'Записи ЭМК',
  'file.linksNone': 'С этим файлом не связано ни одной записи ЭМК.',
  'file.linksAdd': 'Добавить запись ЭМК',
  'file.linkPick': 'Связать с записью…',
  'file.linkExternal': 'Внешняя',
  'file.linkedAt': 'Связан {date}',
  'file.unlinkRecord': 'Отвязать {name}',
  'file.unlinkAction': 'Отвязать',
  'file.linkAddTitle': 'Связать с записью',
  'file.linkRecord': 'Связать с {name}',
  'file.linkAction': 'Связать',
  'file.linkWorking': 'Выполняется',
  'file.linkRecordsLoading': 'Загрузка записей',
  'file.linkRecordsUnavailable': 'Список записей недоступен',
  'file.linkNoneAvailable': 'У этого пациента нет других финальных записей для связывания.',
  'file.linkNoneOnThisPage': 'На этой странице больше записей нет. Покажите более старые записи, чтобы найти ещё.',
  'file.linkMoreRecords': 'Показать более старые записи',
  'file.recordFinalizedAt': 'Финализирована',
  'file.recordStatusFinal': 'Финальная',
  'file.linkForbidden': 'У вас нет прав на изменение связей этого файла с записями.',
  'file.linkGone': 'Не удалось изменить эту связь. Файл или запись больше недоступны.',
  'file.linkUnavailable': 'Сейчас не удалось изменить связь с записью. Попробуйте позже.',

  'file.columnDerived': 'Производные',
  'file.derivedDownloadTranslation': 'Скачать перевод {name}',
  'file.derivedDownloadTranslationIn': 'Скачать перевод {name} на {languages}',
  'file.derivedDownloadDeidentified': 'Скачать анонимизированную копию {name}',
  'file.derivedDownloadOcr': 'Скачать распознанный текст {name}',
  'file.derivedPendingTranslation': 'Перевод выполняется',
  'file.derivedPendingDeidentified': 'Анонимизированная копия создаётся',
  'file.derivedPendingOcr': 'Распознавание текста выполняется',
  'file.derivedAttentionTranslation': 'Перевод недоступен',
  'file.derivedAttentionDeidentified': 'Анонимизированная копия недоступна',
  'file.derivedAttentionOcr': 'Распознанный текст недоступен',
  'file.derivedNoneTranslation': 'Нет перевода',
  'file.derivedNoneDeidentified': 'Нет анонимизированной копии',
  'file.derivedNoneOcr': 'Нет распознанного текста',
  'file.derivedFilterTranslation': 'Есть перевод',
  'file.derivedFilterDeidentified': 'Есть анонимизированная копия',
  'file.derivedFilterOcr': 'Есть распознанный текст',
  'file.derivedFilterNone': 'Нет производных документов',

  'file.derivedTitle': 'Производные документы',
  'file.deriveNotConnected': 'Обработка для этого файла не подключена.',
  'file.deriveLoading': 'Загрузка производных документов',
  'file.deriveUnavailable': 'Производные документы недоступны',
  'file.deriveNone': 'Производных документов пока нет.',
  'file.derivedKindTranslation': 'Перевод',
  'file.derivedKindInLanguage': '{kind} ({language})',
  'file.derivedKindDeidentified': 'Анонимизированная копия',
  'file.derivedKindOcr': 'Распознанный текст',
  'file.deriveDownload': 'Скачать',
  'file.deriveStatePending': 'Обработка',
  'file.deriveStatePurged': 'Удалено',
  'file.deriveStateBroken': 'Сохранённую копию невозможно выдать',
  'file.deriveStateRecreating': 'Создаётся заново',
  'file.deriveStateFailed': 'Последняя попытка не удалась',
  'file.deriveRetry': 'Повторить',
  'file.deriveRetryNamed': 'Повторить: {name}',
  'file.deriveMadeByProcessing': 'Открыть обработку',
  'file.deriveExtractEntries': 'Извлечь записи ЭМК…',
  'file.derivePurge': 'Удалить копию',
  'file.derivePurgeNamed': 'Удалить сохранённую копию {name}',
  'file.derivePurgeConfirm': 'Удалить эту сохранённую копию?',
  'file.derivePurgeYes': 'Удалить',
  'file.derivePurgeKeep': 'Оставить',
  'file.deriveRecreateNamed': 'Воссоздать {name}',
  'file.deriveAdd': 'Добавить производный документ',
  'file.deriveAddSummary': 'Резюме',
  'file.deriveSourceTitle': 'Источник',
  'file.deriveSourceOriginal': 'Из оригинала',
  'file.deriveSourceDeidentified': 'Из анонимизированной копии',
  'file.deriveDeidentify': 'Сначала анонимизировать источник',
  'file.runStatusQueued': 'В очереди',
  'file.runStatusRunning': 'Обработка',
  'file.runStatusRetryWait': 'Повторная попытка',
  'file.runStatusSucceeded': 'Готово',
  'file.runStatusPartiallySucceeded': 'Частично выполнено',
  'file.runStatusFailed': 'Не удалось',
  'file.runStatusCanceled': 'Отменено',
  'file.deriveCancel': 'Отменить обработку',
  'file.deriveCancelRequested': 'Отмена',
  'file.deriveRunFailure': 'Обработка завершилась неудачей',
  'file.failure.canceled': 'Отменено.',
  'file.failure.dependency': 'Не выполнен предыдущий шаг этой обработки.',
  'file.failure.formatUnsupported': 'Файл не открывается: формат не поддерживается, файл повреждён или слишком велик.',
  'file.failure.noText': 'В этом файле не нашлось текста, который можно прочитать.',
  'file.failure.notConfigured': 'Сервис обработки в клинике не настроен.',
  'file.failure.serviceFailed': 'Сбой сервиса обработки — повтор может помочь.',
  'file.failure.integrity': 'Сохранённый файл не совпадает со своей контрольной суммой.',
  'file.failure.tooLarge': 'Файл слишком велик для обработки.',
  'file.failure.provider': 'ИИ-сервис не ответил — повтор может помочь.',
  'file.failure.resultInvalid': 'ИИ-сервис вернул непригодный результат — повтор может помочь.',
  'file.failure.aiUnavailable': 'Эта ИИ-функция в клинике выключена или для неё нет ключа.',
  'file.failure.unknown': 'Обработка не завершилась.',
  'file.deriveStartFailed': 'Не удалось начать обработку. Файл остался без изменений.',
  'file.deriveCancelFailed': 'Не удалось доставить отмену. Обработка продолжается.',
  'file.derivePurgeFailed': 'Не удалось удалить сохранённую копию. Возможно, она ещё используется текущей обработкой.',
  'file.deriveDownloadFailed': 'Не удалось скачать копию. Сохранённые данные остались без изменений.',

  'file.processOne': 'Обработать 1 файл',
  'file.processSelected': 'Обработать файлы ({count})',
  'file.processTitle': 'Обработка файлов',
  'file.processSelection': 'Выбрано',
  'file.processSelectedFolders': 'Папки',
  'file.processSelectedFiles': 'Файлы',
  'file.processSelectedStudies': 'Исследования',
  'file.processFoldersIncluded': 'Включены файлы выбранных папок и всех их подпапок.',
  'file.processActions': 'Что сделать',
  'file.processEntries': 'Извлечь записи ЭМК',
  'file.processEntriesHint': 'ИИ предложит запись по каждому случаю оказания помощи; каждую вы проверите перед сохранением.',
  'file.processSummarize': 'Составить резюме',
  'file.processSummarizeHint': 'Одно резюме по всем файлам на выбранном языке.',
  'file.processTranslate': 'Перевести',
  'file.processTranslateHint': 'Переведённая копия каждого файла на выбранном языке.',
  'file.processOcr': 'Распознать текст',
  'file.processOcrHint': 'Текст сканов и фотографий — для поиска и чтения.',
  'file.processDeidentify': 'Анонимизировать',
  'file.processDeidentifyHint': 'PDF-копия каждого файла без персональных данных пациента.',
  'file.processLanguage': 'Язык результата',
  'file.processChoose': 'Выберите, что сделать с файлами.',
  'file.processStart': 'Запустить',
  'file.processStarting': 'Запуск…',
  'file.processNotReady': 'Пока не готовы к обработке',
  'file.processNone': 'Пока ни один из выбранных файлов не может быть обработан.',
  'file.processSteps': 'Выполнено шагов: {done} из {total}',
  'file.addFile': 'Добавить файл',
  'file.addFolder': 'Добавить папку',
  'file.foldersOpen': 'Дерево каталогов',
  'file.foldersTitle': 'Папки',
  'file.foldersSearch': 'Найти папку или файл',
  'file.foldersSearchPlaceholder': 'Искать среди папок и файлов',
  'file.foldersShowEverything': 'Всё',
  'file.foldersShowFolders': 'Только папки',
  'file.foldersShowFiles': 'Только файлы',
  'file.foldersLoading': 'Читаем папки…',
  'file.foldersFilesLoading': 'Читаем файлы…',
  'file.foldersUnavailable': 'Не удалось прочитать папки.',
  'file.foldersEmpty': 'Папок пока нет. Пока папка не создана, всё лежит одним списком.',
  'file.foldersEmptyAdd':
    'Папок пока нет. Пока папка не создана, всё лежит одним списком. Первую создаст кнопка «Добавить папку» вверху панели.',
  'file.foldersNoMatches': 'Ни одна папка и ни один файл не подходят.',
  'file.foldersLevelEmpty': 'Здесь пока ничего нет.',
  'file.foldersLevelFilesOnly': 'Здесь только файлы, а показаны только папки.',
  'file.foldersAll': 'Все файлы',
  'file.foldersUnfiled': 'Без папки',
  'file.foldersNewName': 'Новая папка',
  'file.foldersAddTop': 'Добавить папку',
  'file.foldersAddHere': 'Добавить папку внутрь',
  'file.foldersRename': 'Переименовать',
  'file.foldersMoveEarlier': 'Выше',
  'file.foldersMoveLater': 'Ниже',
  'file.foldersMoveToTop': 'На верхний уровень',
  'file.foldersDelete': 'Удалить папку',
  'file.foldersOpenFile': 'Открыть',
  'file.foldersUnfile': 'Убрать из папки',
  'file.foldersMenu': 'Действия: {name}',
  'file.foldersDeleteTitle': 'Удалить «{name}»?',
  'file.foldersDeleteDescription': 'Папка и вложенные папки будут удалены. Их файлы и исследования останутся в карте без папки.',
  'file.foldersDeleteCancel': 'Оставить',
  'file.foldersDeleteConfirm': 'Удалить',
  'file.foldersDeleting': 'Удаляем…',
  'file.foldersStale': 'Папки изменили в другом месте, они перечитаны. Повторите действие.',
  'file.foldersNameExists': 'Папка с таким именем здесь уже есть.',
  'file.foldersCycle': 'Папку нельзя переместить внутрь себя самой.',
  'file.foldersDepth': 'Глубже вкладывать папки здесь нельзя.',
  'file.foldersForbidden': 'Вашей учётной записи нельзя менять папки.',
  'file.foldersFailed': 'Не удалось изменить папки.',
  'file.foldersRoot': 'Корень',
  'file.foldersUpTo': 'Наверх: {name}',
  'file.folderOpenNamed': 'Открыть папку {name}',
  'file.columnFolder': 'Папка',
  'file.locationLabel': 'Расположение в папках',
  'file.locationBack': 'Назад',
  'file.locationForward': 'Вперёд',
  'file.locationUp': 'На уровень выше',
  'file.folderNewKey': 'Новая папка (F7)',
  'file.searchDeep': 'Поиск во всех вложенных папках',
  'file.folderCreateTitle': 'Новая папка',
  'file.folderRenameTitle': 'Переименовать папку',
  'file.folderNameLabel': 'Название папки',
  'file.folderNameCancel': 'Отмена',
  'file.folderCreateConfirm': 'Создать',
  'file.folderRenameConfirm': 'Переименовать',
  'file.folderRenameNamed': 'Переименовать папку {name}',
  'file.folderDeleteNamed': 'Удалить папку {name}',
  'file.folderHolds': 'Папок: {folders}, файлов: {files}',
  'file.folderHoldsEmpty': 'Пусто',
  'file.folderEmpty': 'Папка пуста',
  'file.folderEmptyDetail': 'Перетащите сюда файлы или загрузите их, пока открыта эта папка.',
  'file.menuOpen': 'Открыть',
  'file.menuProperties': 'Свойства',
  'file.menuDownload': 'Скачать',
  'file.menuRename': 'Переименовать',
  'file.menuMove': 'Переместить в папку…',
  'file.menuDelete': 'Удалить',
  'file.menuSelected': 'Действия с выбранным: {count}',
  'file.deleteFoldersTitle': 'Папки со всем содержимым',
  'file.deleteFoldersDetail': 'Каждая папка удаляется вместе с вложенными папками, а все файлы и исследования в них удаляются физически, как описано ниже.',
  'file.deleteFoldersReading': 'Читаем содержимое папок…',
  'file.deleteFoldersUnread': 'Не удалось прочитать содержимое папок, поэтому удалить их сейчас нельзя.',
  'file.deleteFoldersBlocked': 'Внутри есть объекты, которые вам нельзя или пока нельзя удалить: {count}. Сначала уберите их из папок.',
  'file.downloadGathering': 'Читаем содержимое выбранных папок…',
  'file.foldersContentsUnread': 'Не удалось прочитать содержимое выбранных папок, поэтому скачать или обработать их сейчас нельзя.',
  'file.foldersContentsRetry': 'Прочитать снова',
  'file.moveSelected': 'В папку: {count}',
  'file.moveSelectedTitle': 'Переместить в папку',
  'file.moveSelectedDescription': 'Выбрано для размещения: {count}. Выберите, куда их положить.',
  'file.moveSelectedTarget': 'Папка',
  'file.moveSelectedUnfiled': 'Без папки',
  'file.moveSelectedSkipped': 'Не войдут: {count} — ещё загружаются или не зарегистрированы, положить их в папку нельзя.',
  'file.moveSelectedCancel': 'Отмена',
  'file.moveSelectedConfirm': 'Переместить: {count}',
  'file.opTypeDeidentify': 'Анонимизация',
  'file.opTypeOcr': 'Распознавание текста',
  'file.opTypeTranslate': 'Перевод',
  'file.opTypeSummarize': 'Резюме',
  'file.opTypeRender': 'Отрисовка документа',
  'file.opTypePurge': 'Очистка',

  'upload.title': 'Добавить медицинский файл',
  'upload.titleBundle': 'Добавить медицинские файлы',
  'upload.limit': 'Принимаются файлы размером до {size}.',
  'upload.filesSelected': 'Выбрано файлов: {count}',
  'upload.filesPartial':
    'Уже сохранено: {stored}. Осталось загрузить: {count}. Сохранённые файлы повторно не отправляются.',
  'upload.progressLabel': 'Ход загрузки',
  'upload.progressIndeterminate': 'Загрузка. Объём отправленного показать невозможно.',
  'upload.progressIndeterminateBundle': 'Загрузка файлов ({count}). Объём отправленного показать невозможно.',
  'upload.unsavedIntent': 'Загрузка файла',
  'upload.fieldTitle': 'Заголовок',
  'upload.fieldServiceDate': 'Дата оказания услуги',
  'upload.fieldFile': 'Файл',
  'upload.fieldFolder': 'Папка',
  'upload.folderUnfiled': 'Без папки',
  'upload.aiClassification': 'Поручить ИИ выбрать группу и содержимое после загрузки',
  'upload.submit': 'Загрузить',
  'upload.submitting': 'Загрузка',
  'upload.accepted': 'Файл принят и добавлен к файлам этого пациента.',
  'upload.acceptedBundle': 'Файлы приняты и добавлены к файлам этого пациента ({count}).',
  'upload.detailsTitle': 'Сведения',
  'upload.detailsHint': 'Необязательно — заполните любые из этих полей или ни одного.',
  'upload.detailsBundleHint': 'Необязательно — заполните любые из этих полей или ни одного. Заполненные сведения применяются ко всем выбранным файлам.',
  'upload.discardTitle': 'Файл не загружен',
  'upload.discardBundleTitle': 'Файлы не загружены',
  'upload.discardDetail': 'Вы начали добавлять этот файл, но не загрузили его.',
  'upload.discardBundleDetail': 'Вы начали добавлять эти файлы, но не загрузили их.',
  'upload.discardRemainingDetail':
    'Перечисленные файлы не загружены. Уже сохранено файлов: {count} — они остаются в файлах пациента.',
  'upload.keepEditing': 'Продолжить редактирование',
  'upload.leavingTitle': 'Загрузка ещё идёт',
  'upload.leavingDetail':
    'Файлы, которые ещё отправляются, будут загружены и появятся в файлах пациента. Закрытие ничего не отменяет, но результат по каждому из них здесь больше не покажут.',
  'upload.leavingWait': 'Подождать здесь',
  'upload.leavingConfirm': 'Всё равно закрыть',
  'upload.discardConfirm': 'Отказаться от этого файла',
  'upload.discardBundleConfirm': 'Отказаться от этих файлов',
  'upload.bundleRefused': 'Не принято файлов: {count} из {total}. Причина указана у каждого файла выше.',
  'upload.unsupportedType': 'Этот файл нельзя загрузить как бинарный файл.',
  'upload.tooLarge': 'Размер этого файла превышает 300 МБ.',
  'upload.tooLargeUnstated': 'Этот файл слишком большой для загрузки.',
  'upload.conflict': 'Эта загрузка уже была отправлена с другими данными. Начните новую загрузку.',
  'upload.invalidInput': 'Проверьте сведения и попробуйте снова.',
  'upload.sessionEnded': 'Ваш сеанс завершён. Войдите снова, чтобы загрузить файл.',
  'upload.forbidden': 'У вас нет прав на добавление файлов этому пациенту.',
  'upload.patientGone': 'Этот пациент больше недоступен.',
  'upload.unavailable': 'Сейчас не удалось загрузить файл. Попробуйте позже.',


  'file.classificationPending': 'Ожидается классификация ИИ',
  'file.classificationFailed': 'Классификация ИИ требует внимания',
  'file.classificationReclassifying': 'Классифицируется заново',

  'emrJournal.title': 'Журнал ЭМК',
  'emrJournal.tableLabel': 'Журнал ЭМК пациента',
  'emrJournal.loading': 'Загрузка журнала ЭМК',
  'emrJournal.loadFailed': 'Не удалось загрузить журнал ЭМК',
  'emrJournal.forbidden': 'У вас нет прав на просмотр записей ЭМК',
  'emrJournal.forbiddenDetail': 'Остальная часть карты пациента по-прежнему доступна.',
  'emrJournal.empty': 'Записей ЭМК пока нет',
  'emrJournal.emptyDetail': 'Здесь появятся приёмы, записанные для этого пациента.',
  'emrJournal.emptyFiltered': 'Нет записей ЭМК, соответствующих этим фильтрам',
  'emrJournal.emptyFilteredDetail': 'Сбросьте фильтры, чтобы прочитать весь журнал.',
  'emrJournal.countOne': '{count} запись',
  'emrJournal.countMany': 'Записей: {count}',
  'emrJournal.columnStarted': 'Дата и время',
  'emrJournal.columnType': 'Тип записи',
  'emrJournal.columnPractitioner': 'Врач',
  'emrJournal.columnStatus': 'Статус',
  'emrJournal.columnCase': 'Случай',
  'emrJournal.columnSections': 'Разделы',
  'emrJournal.typeScheduled': 'Запланированный приём',
  'emrJournal.typeUnscheduled': 'Внеплановый приём',
  'emrJournal.externalManual': 'Вручную',
  'emrJournal.externalAI': 'Из документов (ИИ)',
  'emrJournal.externalNoDate': 'Дата не указана',
  'emrJournal.sectionProtocol': 'Протокол',
  'emrJournal.sectionDiagnoses': 'Диагнозы',
  'emrJournal.sectionAbsent': 'Не начат',
  'emrJournal.sectionIncomplete': 'Не завершён',
  'emrJournal.sectionComplete': 'Завершён',
  'emrJournal.sectionState': '{section}: {state}',
  'emrJournal.sectionFiles': 'Файлы ({count})',
  'emrJournal.filterFrom': 'Начато с',
  'emrJournal.filterTo': 'Начато по',
  'emrJournal.criterionStarted': 'Сопоставляется с отображаемым временем начала и кодом приёма.',
  'emrJournal.criterionCase': 'Сопоставляется с кодом случая и его названием.',
  'emrJournal.caseFacetOption': '{code} — {title}',
  'emrJournal.filterEnteredInError': 'Показывать внесённые по ошибке',

  'emrEntry.title': 'Добавить запись ЭМК',
  'emrEntry.branch': 'Место приёма',
  'emrEntry.workplaceRequired': 'Выберите место работы',
  'emrEntry.workplaceUnavailable': 'Место работы изменилось или больше недоступно. Выберите доступное место работы заново.',
  'emrEntry.workplace': 'Место работы',
  'emrEntry.workplaceNone': 'не выбрано',
  'emrEntry.workplaceChange': 'Сменить',
  'emrEntry.workplaceChangeLabel': 'Сменить место работы',
  'emrEntry.workplaceChoose': 'Выбрать место работы',
  'emrEntry.branchRequired': 'Выберите филиал',
  'emrEntry.branchUnavailable': 'Этот филиал больше недоступен. Выберите другой филиал.',
  'emrEntry.detail': 'Новый приём и его основной протокол создаются вместе.',
  'emrEntry.placeLegend': 'Где оказана помощь',
  'emrEntry.placeClinic': 'В нашей клинике',
  'emrEntry.placeClinicHint': 'Начинает приём с основным протоколом в рамках случая.',
  'emrEntry.placeOutside': 'В другой клинике',
  'emrEntry.placeOutsideHint': 'Фиксирует помощь, полученную пациентом вне клиники, по протоколу внешней записи.',
  'emrEntry.externalDetail': 'Создаётся черновик внешней записи и сразу открывается: заполните и завершите его там.',
  'emrEntry.externalProtocolFixed': 'Помощь вне клиники всегда записывается по этому протоколу.',
  'emrEntry.caseLegend': 'Случай',
  'emrEntry.caseNoneOpen': 'У этого пациента нет открытого случая, поэтому эта запись создаёт новый.',
  'emrEntry.caseChoose': 'Выберите случай, к которому относится эта запись.',
  'emrEntry.submit': 'Создать запись',
  'emrEntry.submitting': 'Создание…',
  'emrEntry.failed': 'Не удалось создать запись ЭМК. Попробуйте снова.',
  'emrEntry.forbidden': 'У вас нет прав на добавление записи ЭМК.',
  'emrEntry.unavailable': 'Сервис недоступен. Попробуйте чуть позже.',
  'emrEntry.conflict': 'Запись ЭМК не создана. Перезагрузите журнал и попробуйте снова.',
  'emrEntry.caseUnavailable': 'В этот случай больше нельзя внести новую запись. Выберите другой или создайте новый.',
  'emrEntry.gone': 'Этот пациент больше недоступен.',
  'emrEntry.sessionEnded': 'Ваш сеанс завершён. Войдите снова, чтобы продолжить.',

  'file.group': 'Группа',
  'file.content': 'Содержимое',
  'file.groupUnchosen': 'Выберите группу',
  'file.contentUnchosen': 'Выберите содержимое',
  'provenance.human': 'Выбрано человеком',
  'provenance.humanDraft': 'Выбрано вами. Ещё не сохранено.',
  'provenance.ai': 'Выбрано ИИ',
  'provenance.aiConfirmed': 'Выбрано ИИ, подтверждено человеком',
  'provenance.aiConfirmedDraft': 'Выбрано ИИ, подтверждено вами. Ещё не сохранено.',
  'provenance.system': 'Задано системой',
  'provenance.unknown': 'Источник выбора не записан',
  'fileGroup.medical': 'Медицинские',
  'fileGroup.financial': 'Финансовые',
  'fileGroup.identity': 'Удостоверяющие личность',
  'fileGroup.administrative': 'Административные',
  'fileGroup.collection': 'Сборник документов',
  'fileGroup.other': 'Прочие',
  'fileContent.medical_conclusion': 'Заключение врача',
  'fileContent.discharge_summary': 'Выписка / эпикриз',
  'fileContent.clinical_referral': 'Направление',
  'fileContent.laboratory_results': 'Результаты анализов',
  'fileContent.study_report': 'Заключение исследования',
  'fileContent.medical_images': 'Медицинские изображения',
  'fileContent.prescription': 'Рецепт',
  'fileContent.treatment_plan': 'План лечения',
  'fileContent.vaccination_record': 'Сведения о прививках',
  'fileContent.medical_summary': 'Медицинское резюме',
  'fileContent.invoice': 'Счёт',
  'fileContent.treatment_cost_estimate': 'Смета лечения',
  'fileContent.payment_guarantee': 'Гарантия оплаты',
  'fileContent.payment_receipt': 'Подтверждение оплаты',
  'fileContent.insurance_policy': 'Страховой полис',
  'fileContent.passport': 'Паспорт',
  'fileContent.identity_card': 'Удостоверение личности',
  'fileContent.driving_licence': 'Водительское удостоверение',
  'fileContent.residence_permit': 'Вид на жительство',
  'fileContent.agreement': 'Договор',
  'fileContent.consent': 'Согласие',
  'fileContent.appointment_confirmation': 'Подтверждение приёма',
  'fileContent.travel_booking': 'Билеты и бронирования',
  'fileContent.correspondence': 'Переписка',
}
